import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

function isItemActive(location, item) {
  if (item.matchPaths?.some((p) => location.pathname.startsWith(p))) {
    return true;
  }
  if (!item.path) return false;
  return (
    location.pathname === item.path ||
    location.pathname.startsWith(`${item.path}/`)
  );
}

function groupHasActive(location, group) {
  return group.items?.some((item) => isItemActive(location, item));
}

function buildInitialOpenState(groups, location) {
  const state = {};
  groups.forEach((group) => {
    if (!group.collapsible) return;
    state[group.id] =
      group.defaultOpen === true || groupHasActive(location, group);
  });
  return state;
}

/**
 * Meta-style grouped sidebar navigation with collapsible sections.
 */
export default function SidebarNav({ groups, onNavigate }) {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState(() =>
    buildInitialOpenState(groups, location),
  );

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      groups.forEach((group) => {
        if (group.collapsible && groupHasActive(location, group) && !next[group.id]) {
          next[group.id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname, groups]);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const visibleGroups = useMemo(
    () => groups.filter((g) => g.items?.length > 0),
    [groups],
  );

  return (
    <section className="routes">
      {visibleGroups.map((group) => {
        const isOpen = !!openGroups[group.id];
        const groupActive = groupHasActive(location, group);

        if (group.collapsible) {
          return (
            <div className="nf-nav-section" key={group.id}>
              <ul className="navbar-nav">
                <li
                  className={`nav-item nf-nav-group${isOpen ? " is-open" : ""}`}
                >
                  <button
                    type="button"
                    className={`nav-link d-flex align-items-center w-100 nf-nav-group-toggle nf-nav-group-heading${groupActive ? " is-child-active" : ""}`}
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={isOpen}
                    title={group.label}
                  >
                    {group.icon ? <div className="icon">{group.icon}</div> : null}
                    <div className="link_text nf-nav-group-title">{group.label}</div>
                    <i
                      className={`fas fa-chevron-${isOpen ? "up" : "down"} nf-nav-chevron`}
                      aria-hidden="true"
                    />
                  </button>
                  <ul className="navbar-nav nf-nav-sub">
                    {group.items.map((item) => (
                      <li className="nav-item" key={item.path || item.name}>
                        <NavLink
                          to={item.path}
                          className={({ isActive }) =>
                            `nav-link d-flex align-items-center nf-nav-sub-link${isActive || isItemActive(location, item) ? " active" : ""}`
                          }
                          onClick={onNavigate}
                        >
                          {item.icon ? (
                            <div className="icon">{item.icon}</div>
                          ) : null}
                          <div className="link_text">{item.name}</div>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              </ul>
            </div>
          );
        }

        return (
          <div className="nf-nav-section" key={group.id}>
            {group.label ? (
              <div className="nf-sidebar-section-label">{group.label}</div>
            ) : null}
            <ul className="navbar-nav">
              {group.items.map((item) => (
                <li className="nav-item" key={item.path || item.name}>
                  {item.action ? (
                    <button
                      type="button"
                      className="nav-link d-flex align-items-center w-100 border-0 bg-transparent text-start"
                      onClick={() => {
                        item.action();
                        onNavigate?.();
                      }}
                    >
                      {item.icon ? <div className="icon">{item.icon}</div> : null}
                      <div className="link_text">{item.name}</div>
                    </button>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `nav-link d-flex align-items-center${isActive || isItemActive(location, item) ? " active" : ""}`
                      }
                      onClick={onNavigate}
                    >
                      {item.icon ? <div className="icon">{item.icon}</div> : null}
                      <div className="link_text">{item.name}</div>
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
}
